import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PRIORITY_COLORS, STATUS_COLORS, STATUS_LABELS } from '../UserContext';

const API = `${window.location.protocol}//${window.location.hostname}/api`;

function api(path, opts = {}) {
  const token = localStorage.getItem('token');
  return fetch(`${API}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...opts.headers },
  }).then(async r => {
    const data = await r.json().catch(() => null);
    if (!r.ok) throw new Error(data?.error || 'Request failed');
    return data;
  });
}

const TABS = [
  { key: 'projects',    label: 'Projects' },
  { key: 'incidents',   label: 'Incidents' },
  { key: 'inspections', label: 'Inspections' },
  { key: 'completed',   label: 'Completed' },
  { key: 'performance', label: 'My Performance' },
];

const ACTIVITY_TYPES = [
  { value: 'drive',        label: 'Drive to Site' },
  { value: 'on_site',      label: 'On Site' },
  { value: 'bench',        label: 'Bench Work' },
  { value: 'testing',      label: 'Testing' },
  { value: 'admin',        label: 'Admin' },
  { value: 'return_drive', label: 'Return Drive' },
];

function badgeStyle(color) {
  return {
    fontSize: 10, fontWeight: 800, textTransform: 'uppercase',
    padding: '3px 7px', borderRadius: 5,
    backgroundColor: `${color}22`, color, border: `1px solid ${color}44`,
  };
}

function AcceptanceBadge({ status }) {
  const colors = { pending: '#f59e0b', accepted: '#22c55e', rejected: '#ef4444', not_required: '#64748b' };
  const labels = { pending: 'Pending', accepted: 'Accepted', rejected: 'Rejected', not_required: 'Not Required' };
  const color = colors[status] || '#64748b';
  return <span style={badgeStyle(color)}>{labels[status] || status}</span>;
}

function formatElapsed(startedAt) {
  const elapsed = Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000);
  const h = Math.floor(elapsed / 3600);
  const m = Math.floor((elapsed % 3600) / 60);
  const s = elapsed % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function TimerWidget({ items, onRefresh }) {
  const [open, setOpen] = useState(null);
  const [elapsed, setElapsed] = useState('');
  const [showStart, setShowStart] = useState(false);
  const [workItemId, setWorkItemId] = useState('');
  const [activityType, setActivityType] = useState('drive');
  const [notes, setNotes] = useState('');
  const [timerError, setTimerError] = useState('');
  const [benchPrompt, setBenchPrompt] = useState(null); // { workItemId, title, mapsUrl }
  const intervalRef = useRef(null);

  const loadOpen = useCallback(() => {
    api('/time-entries/open').then(entry => {
      setOpen(entry);
      if (entry) setElapsed(formatElapsed(entry.started_at));
    }).catch(() => {});
  }, []);

  useEffect(() => { loadOpen(); }, [loadOpen]);

  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (open) {
      intervalRef.current = setInterval(() => setElapsed(formatElapsed(open.started_at)), 1000);
    }
    return () => clearInterval(intervalRef.current);
  }, [open]);

  const startTimer = async () => {
    if (!workItemId) { setTimerError('Select a work order'); return; }
    setTimerError('');
    try {
      const entry = await api('/time-entries/start', {
        method: 'POST',
        body: JSON.stringify({ work_item_id: Number(workItemId), activity_type: activityType, notes: notes || null }),
      });
      setOpen(entry);
      setElapsed(formatElapsed(entry.started_at));
      setShowStart(false);
      setNotes('');
    } catch (err) {
      setTimerError(err.message);
    }
  };

  const stopTimer = async () => {
    setTimerError('');
    try {
      const wasBench = open?.activity_type === 'bench';
      const stoppedWorkItemId = open?.work_item_id;
      const stoppedItem = activeItems.find(i => String(i.id) === String(stoppedWorkItemId));
      await api(`/time-entries/${open.id}/stop`, { method: 'PATCH', body: JSON.stringify({}) });
      setOpen(null);
      setElapsed('');
      onRefresh();
      if (wasBench && stoppedItem) {
        const url = stoppedItem.latitude && stoppedItem.longitude
          ? `https://maps.google.com/?q=${stoppedItem.latitude},${stoppedItem.longitude}`
          : stoppedItem.location_name
          ? `https://maps.google.com/?q=${encodeURIComponent(stoppedItem.location_name)}`
          : null;
        setBenchPrompt({ workItemId: stoppedWorkItemId, title: stoppedItem.title, mapsUrl: url });
      }
    } catch (err) {
      setTimerError(err.message);
    }
  };

  const startDriveFromBench = async () => {
    if (!benchPrompt) return;
    try {
      const entry = await api('/time-entries/start', {
        method: 'POST',
        body: JSON.stringify({ work_item_id: Number(benchPrompt.workItemId), activity_type: 'drive', notes: 'Drive from bench to site' }),
      });
      setOpen(entry);
      setElapsed(formatElapsed(entry.started_at));
    } catch (err) {
      setTimerError(err.message);
    }
    setBenchPrompt(null);
  };

  const activeItems = items.filter(i => i.tech_acceptance_status === 'accepted' || i.tech_acceptance_status === 'not_required');
  const selectedItem = activeItems.find(i => String(i.id) === String(workItemId));
  const mapsUrl = activityType === 'drive' && selectedItem?.latitude && selectedItem?.longitude
    ? `https://maps.google.com/?q=${selectedItem.latitude},${selectedItem.longitude}`
    : activityType === 'drive' && selectedItem?.location_name
    ? `https://maps.google.com/?q=${encodeURIComponent(selectedItem.location_name)}`
    : null;
  const activityLabel = ACTIVITY_TYPES.find(a => a.value === open?.activity_type)?.label || open?.activity_type;

  const panelStyle = {
    margin: '0 20px 0',
    border: open ? '1px solid #16a34a44' : '1px solid #334155',
    borderRadius: 8,
    backgroundColor: open ? '#052e16' : '#111827',
    padding: '12px 14px',
  };

  const inputStyle = {
    padding: '8px 10px', backgroundColor: '#0f172a', border: '1px solid #334155',
    borderRadius: 6, color: '#e2e8f0', fontSize: 13, width: '100%', boxSizing: 'border-box',
  };

  return (
    <div style={panelStyle}>
      {open ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: '#4ade80', fontSize: 11, fontWeight: 800, textTransform: 'uppercase', marginBottom: 2 }}>
              Timer Running
            </div>
            <div style={{ color: '#f8fafc', fontWeight: 800, fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {open.title}
            </div>
            <div style={{ color: '#86efac', fontSize: 12, marginTop: 3 }}>
              {activityLabel} · started {new Date(open.started_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
          <div style={{ fontFamily: 'monospace', fontSize: 22, fontWeight: 800, color: '#4ade80', letterSpacing: '0.05em' }}>
            {elapsed}
          </div>
          <button
            onClick={stopTimer}
            style={{ padding: '8px 16px', backgroundColor: '#7f1d1d', color: '#fecaca', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 800, fontSize: 13 }}
          >
            Stop
          </button>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ color: '#64748b', fontSize: 13 }}>No active timer</div>
            <button
              onClick={() => { setShowStart(s => !s); setTimerError(''); }}
              style={{ padding: '7px 13px', backgroundColor: '#166534', color: '#dcfce7', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 800, fontSize: 13 }}
            >
              Start Timer
            </button>
          </div>
          {showStart && (
            <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 5 }}>Work Order</div>
                <select value={workItemId} onChange={e => setWorkItemId(e.target.value)} style={inputStyle}>
                  <option value="">Select work order…</option>
                  {activeItems.map(i => (
                    <option key={i.id} value={i.id}>
                      {i.wo_number ? `WO ${i.wo_number} — ` : ''}{i.title}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 5 }}>Activity</div>
                <select value={activityType} onChange={e => setActivityType(e.target.value)} style={inputStyle}>
                  {ACTIVITY_TYPES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                </select>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 5 }}>Notes (optional)</div>
                <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. driving from yard" style={inputStyle} />
              </div>
              {mapsUrl && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <a href={mapsUrl} target="_blank" rel="noreferrer" style={{ display: 'inline-block', padding: '7px 14px', backgroundColor: '#0f172a', border: '1px solid #334155', color: '#93c5fd', borderRadius: 6, textDecoration: 'none', fontSize: 13, fontWeight: 700 }}>
                    📍 Navigate to Site
                  </a>
                </div>
              )}
              {timerError && (
                <div style={{ gridColumn: '1 / -1', color: '#f87171', fontSize: 13, fontWeight: 700 }}>{timerError}</div>
              )}
              <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button onClick={() => setShowStart(false)} style={{ padding: '8px 14px', backgroundColor: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 6, cursor: 'pointer' }}>Cancel</button>
                <button onClick={startTimer} style={{ padding: '8px 16px', backgroundColor: '#166534', color: '#dcfce7', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 800 }}>Start</button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Bench → Drive prompt */}
      {benchPrompt && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.75)', zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ width: '100%', maxWidth: 420, backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: 24 }}>
            <div style={{ color: '#f8fafc', fontWeight: 800, fontSize: 17, marginBottom: 6 }}>Bench work complete</div>
            <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 20 }}>
              Start a Drive to Site leg for <strong style={{ color: '#e2e8f0' }}>{benchPrompt.title}</strong>?
            </div>
            {benchPrompt.mapsUrl && (
              <a href={benchPrompt.mapsUrl} target="_blank" rel="noreferrer"
                style={{ display: 'inline-block', marginBottom: 16, padding: '7px 14px', backgroundColor: '#0f172a', border: '1px solid #334155', color: '#93c5fd', borderRadius: 6, textDecoration: 'none', fontSize: 13, fontWeight: 700 }}>
                📍 Navigate to Site
              </a>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setBenchPrompt(null)} style={{ flex: 1, padding: 10, backgroundColor: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 6, cursor: 'pointer' }}>
                No, I'm done
              </button>
              <button onClick={startDriveFromBench} style={{ flex: 2, padding: 10, backgroundColor: '#166534', color: '#dcfce7', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 800 }}>
                Yes — Start Drive Timer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function WorkCard({ item, onAccept, onReject, onOpen }) {
  const statusColor = STATUS_COLORS[item.status] || '#64748b';
  const priorityColor = PRIORITY_COLORS[item.priority] || '#64748b';
  const canRespond = item.tech_acceptance_status === 'pending' || item.tech_acceptance_status === 'not_required';

  return (
    <div style={{ backgroundColor: '#1e293b', border: `1px solid ${statusColor}44`, borderLeft: `4px solid ${statusColor}`, borderRadius: 8, padding: 14, marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
        <button onClick={() => onOpen(item.id)} style={{ flex: 1, minWidth: 0, padding: 0, border: 'none', background: 'transparent', textAlign: 'left', cursor: 'pointer' }}>
          <div style={{ color: '#f8fafc', fontWeight: 800, fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.title}</div>
          <div style={{ color: '#64748b', fontSize: 12, marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {item.wo_number && <span>WO {item.wo_number}</span>}
            {item.po_number && <span>PO {item.po_number}</span>}
            {item.sn_ticket && <span>SN {item.sn_ticket}</span>}
            {item.location_name && <span>{item.location_name}</span>}
          </div>
        </button>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <AcceptanceBadge status={item.tech_acceptance_status} />
          <span style={badgeStyle(priorityColor)}>{item.priority || 'normal'}</span>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(110px, 1fr))', gap: 8, marginTop: 12 }}>
        {[
          ['Status', STATUS_LABELS[item.status] || item.status],
          ['Scheduled', item.scheduled_date ? new Date(item.scheduled_date).toLocaleDateString() : '--'],
          ['Category', item.work_category || '--'],
          ['Required', Array.isArray(item.required_skills) && item.required_skills.length ? item.required_skills.join(', ') : '--'],
        ].map(([label, value]) => (
          <div key={label} style={{ backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 6, padding: '8px 10px', minWidth: 0 }}>
            <div style={{ color: '#475569', fontSize: 10, fontWeight: 800, textTransform: 'uppercase' }}>{label}</div>
            <div style={{ color: '#cbd5e1', fontSize: 12, fontWeight: 700, marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</div>
          </div>
        ))}
      </div>

      {item.tech_acceptance_status === 'rejected' && item.tech_reject_reason && (
        <div style={{ marginTop: 10, color: '#fca5a5', backgroundColor: '#450a0a', border: '1px solid #7f1d1d', borderRadius: 6, padding: '8px 10px', fontSize: 12 }}>
          Rejected: {item.tech_reject_reason}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
        <button onClick={() => onOpen(item.id)} style={{ padding: '7px 11px', backgroundColor: '#0f172a', color: '#93c5fd', border: '1px solid #1d4ed8', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
          Open
        </button>
        {canRespond && (
          <>
            <button onClick={() => onReject(item)} style={{ padding: '7px 11px', backgroundColor: '#7f1d1d', color: '#fecaca', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 800 }}>
              Reject
            </button>
            <button onClick={() => onAccept(item)} style={{ padding: '7px 11px', backgroundColor: '#166534', color: '#dcfce7', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 800 }}>
              Accept
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function MyPerformance() {
  const [jhas, setJhas] = useState([]);
  const [timesheet, setTimesheet] = useState([]);
  const [verifs, setVerifs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api('/jhas?mine=true').catch(() => []),
      api('/time-entries/timesheet?date_from=' + new Date(new Date().setDate(1)).toISOString().slice(0, 10) + '&date_to=' + new Date().toISOString().slice(0, 10)).catch(() => []),
    ]).then(([jhaRows, tsRows]) => {
      setJhas(Array.isArray(jhaRows) ? jhaRows : []);
      setTimesheet(Array.isArray(tsRows) ? tsRows : []);
      setLoading(false);
    });
  }, []);

  if (loading) return <div style={{ color: '#64748b', padding: 20, textAlign: 'center' }}>Loading…</div>;

  const submitted = jhas.filter(j => j.jha_submitted_at).length;
  const lags = jhas.filter(j => j.jha_lag_minutes != null).map(j => j.jha_lag_minutes);
  const avgLag = lags.length ? Math.round(lags.reduce((s, v) => s + v, 0) / lags.length) : null;
  const totalHours = timesheet.reduce((s, r) => s + (r.total_hours || 0), 0);
  const otHours = timesheet.reduce((s, r) => s + (r.ot_hours || 0), 0);

  const card = (label, value, color) => (
    <div style={{ backgroundColor: '#1e293b', border: `1px solid ${color}22`, borderTop: `3px solid ${color}`, borderRadius: 10, padding: '14px 16px' }}>
      <div style={{ color: '#64748b', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
      <div style={{ color, fontSize: 24, fontWeight: 800 }}>{value ?? '—'}</div>
    </div>
  );

  return (
    <div style={{ maxWidth: 800 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 20 }}>
        {card('JHAs Completed', jhas.length, '#3b82f6')}
        {card('JHAs Submitted', submitted, '#22c55e')}
        {card('Avg JHA Lag', avgLag != null ? `${avgLag}m` : '—', avgLag != null && avgLag > 30 ? '#ef4444' : '#22c55e')}
        {card('Hours This Month', totalHours.toFixed(1), '#8b5cf6')}
        {card('OT Hours', otHours.toFixed(1), otHours > 0 ? '#f59e0b' : '#64748b')}
      </div>

      {jhas.length > 0 && (
        <div style={{ border: '1px solid #1e293b', borderRadius: 8, overflow: 'hidden', marginBottom: 16 }}>
          <div style={{ padding: '10px 14px', backgroundColor: '#111827', color: '#cbd5e1', fontSize: 13, fontWeight: 800 }}>My JHAs</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ backgroundColor: '#111827' }}>
                {['Work Order', 'Station', 'Status', 'Submitted', 'Lag (min)'].map(h => (
                  <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: '#64748b', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', borderBottom: '1px solid #1e293b' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {jhas.map(j => {
                const lagColor = j.jha_lag_minutes == null ? '#64748b' : j.jha_lag_minutes <= 15 ? '#22c55e' : j.jha_lag_minutes <= 30 ? '#f59e0b' : '#ef4444';
                return (
                  <tr key={j.id} style={{ borderBottom: '1px solid #111827' }}>
                    <td style={{ padding: '8px 12px', color: '#94a3b8' }}>{j.work_item_title || `JHA-${j.id}`}</td>
                    <td style={{ padding: '8px 12px', color: '#94a3b8' }}>{j.station || '—'}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 4, backgroundColor: '#1e293b', color: '#94a3b8' }}>{j.status?.replace(/_/g, ' ')}</span>
                    </td>
                    <td style={{ padding: '8px 12px', color: '#64748b' }}>{j.jha_submitted_at ? new Date(j.jha_submitted_at).toLocaleDateString() : '—'}</td>
                    <td style={{ padding: '8px 12px', color: lagColor, fontWeight: 700 }}>{j.jha_lag_minutes ?? '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const pinInp = { width: '100%', padding: '9px 10px', backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 7, color: '#e2e8f0', fontSize: 14, boxSizing: 'border-box', marginBottom: 10 };

function PinSetter() {
  const [open, setOpen] = useState(false);
  const [pin, setPin] = useState('');
  const [confirm, setConfirm] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (pin.length < 4) { setMsg('PIN must be at least 4 digits'); return; }
    if (pin !== confirm) { setMsg('PINs do not match'); return; }
    setBusy(true); setMsg('');
    try {
      const me = await api('/auth/me');
      await api(`/users/${me.id}/jha-pin`, { method: 'POST', body: JSON.stringify({ pin }) });
      setMsg('✓ PIN saved'); setPin(''); setConfirm('');
      setTimeout(() => { setOpen(false); setMsg(''); }, 1200);
    } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} style={{ fontSize: 12, padding: '6px 12px', backgroundColor: '#1e293b', color: '#94a3b8', border: '1px solid #334155', borderRadius: 6, cursor: 'pointer' }}>🔐 JHA PIN</button>
      {open && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ width: '100%', maxWidth: 360, backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: 22 }}>
            <div style={{ color: '#f8fafc', fontWeight: 800, fontSize: 17, marginBottom: 6 }}>Set your JHA PIN</div>
            <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 14 }}>Signs JHAs as your verified digital signature. 4+ digits.</div>
            <input type="password" inputMode="numeric" value={pin} onChange={e => setPin(e.target.value)} placeholder="New PIN" style={pinInp} />
            <input type="password" inputMode="numeric" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Confirm PIN" style={pinInp} />
            {msg && <div style={{ fontSize: 12, color: msg.startsWith('✓') ? '#4ade80' : '#f87171', marginBottom: 10 }}>{msg}</div>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => { setOpen(false); setMsg(''); }} style={{ flex: 1, padding: 10, backgroundColor: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 6, cursor: 'pointer' }}>Cancel</button>
              <button onClick={save} disabled={busy} style={{ flex: 2, padding: 10, backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 800 }}>{busy ? 'Saving…' : 'Save PIN'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function MyWork() {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], projects: [], incidents: [], inspections: [] });
  const [tab, setTab] = useState('projects');
  const [loading, setLoading] = useState(true);
  const [includeRejected, setIncludeRejected] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [rejecting, setRejecting] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  // Callout state
  const [pendingCallouts, setPendingCallouts] = useState([]);
  const [calloutRejecting, setCalloutRejecting] = useState(null);
  const [calloutReason, setCalloutReason] = useState('');
  const [pushStatus, setPushStatus] = useState('idle'); // idle | subscribed | denied | unsupported

  // Material to receive (3b)
  const [receipts, setReceipts] = useState([]);
  const [returnTasks, setReturnTasks] = useState([]);
  const [receivingShip, setReceivingShip] = useState(null);
  const [recvItems, setRecvItems] = useState([]);
  const [recvSig, setRecvSig] = useState('');
  const [recvBusy, setRecvBusy] = useState(false);

  const load = useCallback(() => {
    setError('');
    return api(`/work-items/my-work${includeRejected ? '?include_rejected=true' : ''}`)
      .then(next => {
        setData({
          items: next.items || [],
          projects: next.projects || [],
          incidents: next.incidents || [],
          inspections: next.inspections || [],
          completed: next.completed || [],
        });
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [includeRejected]);

  useEffect(() => { load(); }, [load]);

  const loadReturnTasks = useCallback(() => {
    api('/materials/return-tasks/mine').then(r => setReturnTasks(Array.isArray(r) ? r : [])).catch(() => {});
  }, []);
  useEffect(() => { loadReturnTasks(); }, [loadReturnTasks]);

  const acceptReturn = async (t) => {
    await api(`/materials/return-tasks/${t.id}/start`, { method: 'PATCH' }).catch(e => alert(e.message));
    loadReturnTasks();
  };
  const driveToStock = (t) => {
    const sl = t.stock_location;
    if (!sl) { alert('No stock location set up yet.'); return; }
    if (sl.latitude && sl.longitude) window.open(`https://maps.google.com/?q=${sl.latitude},${sl.longitude}`, '_blank');
    api('/time-entries/start', { method: 'POST', body: JSON.stringify({ work_item_id: t.work_item_id, activity_type: 'return_drive', notes: 'Return drive to stock' }) }).catch(() => {});
  };

  const loadCallouts = useCallback(() => {
    api('/callouts/pending').then(setPendingCallouts).catch(() => {});
  }, []);

  useEffect(() => {
    loadCallouts();
    const id = setInterval(loadCallouts, 15000);
    return () => clearInterval(id);
  }, [loadCallouts]);

  // Respond to WS callout_received
  useEffect(() => {
    const handler = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.type === 'callout_received') loadCallouts();
      } catch {}
    };
    window.__fcWs?.addEventListener('message', handler);
    return () => window.__fcWs?.removeEventListener('message', handler);
  }, [loadCallouts]);

  // ── Material to receive (3b) ──────────────────────────────
  const loadReceipts = useCallback(() => {
    api('/materials/pending-receipts').then(setReceipts).catch(() => {});
  }, []);
  useEffect(() => {
    loadReceipts();
    const id = setInterval(loadReceipts, 20000);
    return () => clearInterval(id);
  }, [loadReceipts]);
  useEffect(() => {
    const handler = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (['material_delivered', 'material_received'].includes(msg.type)) loadReceipts();
      } catch {}
    };
    window.__fcWs?.addEventListener('message', handler);
    return () => window.__fcWs?.removeEventListener('message', handler);
  }, [loadReceipts]);

  const openReceive = (ship) => {
    setReceivingShip(ship);
    setRecvItems((ship.items || []).map(i => ({ id: i.id, received_qty: i.expected_qty, verified: true })));
    setRecvSig('');
  };
  const submitReceive = async () => {
    setRecvBusy(true);
    try {
      await api(`/materials/${receivingShip.id}/receive`, {
        method: 'PATCH',
        body: JSON.stringify({ signature: recvSig || null, items: recvItems }),
      });
      setReceivingShip(null);
      loadReceipts();
    } catch (err) { setStatus(err.message); } finally { setRecvBusy(false); }
  };

  // Check current push subscription state on load
  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setPushStatus('unsupported');
      return;
    }
    navigator.serviceWorker.ready.then(reg => {
      reg.pushManager.getSubscription().then(sub => {
        setPushStatus(sub ? 'subscribed' : 'idle');
      });
    });
  }, []);

  const enablePush = async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    try {
      const { publicKey } = await api('/callouts/vapid-public-key');
      const reg = await navigator.serviceWorker.ready;
      const raw = atob(publicKey.replace(/-/g, '+').replace(/_/g, '/'));
      const keyBytes = new Uint8Array(raw.length);
      for (let i = 0; i < raw.length; i++) keyBytes[i] = raw.charCodeAt(i);

      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: keyBytes,
      });
      await api('/callouts/subscribe', { method: 'POST', body: JSON.stringify({ subscription: sub.toJSON() }) });
      setPushStatus('subscribed');
    } catch (err) {
      if (err.name === 'NotAllowedError') setPushStatus('denied');
    }
  };

  const disablePush = async () => {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      await api('/callouts/unsubscribe', { method: 'DELETE', body: JSON.stringify({ endpoint: sub.endpoint }) });
      await sub.unsubscribe();
    }
    setPushStatus('idle');
  };

  const respondCallout = async (calloutId, status, reason) => {
    try {
      await api(`/callouts/${calloutId}/respond`, {
        method: 'PATCH',
        body: JSON.stringify({ status, reason: reason || undefined }),
      });
      setCalloutRejecting(null);
      setCalloutReason('');
      loadCallouts();
    } catch (err) {
      setError(err.message);
    }
  };

  const counts = useMemo(() => ({
    pending: data.items.filter(item => item.tech_acceptance_status === 'pending').length,
    accepted: data.items.filter(item => item.tech_acceptance_status === 'accepted').length,
    rejected: data.items.filter(item => item.tech_acceptance_status === 'rejected').length,
  }), [data.items]);

  const accept = async (item) => {
    setError('');
    setStatus('');
    try {
      await api(`/work-items/${item.id}/accept`, { method: 'POST', body: JSON.stringify({}) });
      setStatus('Assignment accepted');
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const submitReject = async () => {
    if (!rejecting) return;
    if (!rejectReason.trim()) { setError('Reject reason required'); return; }
    setError('');
    setStatus('');
    try {
      await api(`/work-items/${rejecting.id}/reject`, { method: 'POST', body: JSON.stringify({ reason: rejectReason }) });
      setStatus('Assignment rejected');
      setRejecting(null);
      setRejectReason('');
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <div style={{ flex: 1, backgroundColor: '#0f172a', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'sans-serif' }}>
        Loading my work...
      </div>
    );
  }

  const visible = data[tab] || [];

  return (
    <div style={{ flex: 1, overflow: 'hidden', backgroundColor: '#0f172a', color: '#e2e8f0', fontFamily: 'sans-serif', display: 'flex', flexDirection: 'column' }}>
      <div style={{ borderBottom: '1px solid #1e293b', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 800, color: '#f8fafc' }}>My Work</div>
          <div style={{ color: '#64748b', fontSize: 13, marginTop: 3 }}>
            {counts.pending} pending · {counts.accepted} accepted{includeRejected ? ` · ${counts.rejected} rejected` : ''}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <PinSetter />
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#94a3b8', fontSize: 13 }}>
            <input type="checkbox" checked={includeRejected} onChange={e => setIncludeRejected(e.target.checked)} />
            Show rejected
          </label>
          {pushStatus !== 'unsupported' && (
            pushStatus === 'subscribed' ? (
              <button onClick={disablePush} style={{ fontSize: 12, padding: '6px 12px', backgroundColor: '#1e293b', color: '#22c55e', border: '1px solid #22c55e44', borderRadius: 6, cursor: 'pointer' }}>
                🔔 Alerts On
              </button>
            ) : pushStatus === 'denied' ? (
              <span style={{ fontSize: 12, color: '#64748b' }}>🔕 Notifications blocked</span>
            ) : (
              <button onClick={enablePush} style={{ fontSize: 12, padding: '6px 12px', backgroundColor: '#1e293b', color: '#94a3b8', border: '1px solid #334155', borderRadius: 6, cursor: 'pointer' }}>
                🔔 Enable Callout Alerts
              </button>
            )
          )}
        </div>
      </div>

      {/* ── Pending Callout Banners ─────────────────────────────── */}
      {pendingCallouts.map(c => (
        <div key={c.id} style={{
          margin: '0 20px 0', borderRadius: 10, padding: '14px 16px',
          backgroundColor: '#450a0a', border: '1px solid #ef4444',
          marginTop: 12,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
            <div>
              <div style={{ color: '#ef4444', fontWeight: 800, fontSize: 13, textTransform: 'uppercase', letterSpacing: 1 }}>
                🚨 Emergency Callout — {c.priority?.toUpperCase()}
              </div>
              <div style={{ color: '#fca5a5', fontWeight: 600, fontSize: 14, marginTop: 2 }}>
                {c.work_order_number || `WO-${c.work_item_id}`} — {c.title}
              </div>
              <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 2 }}>
                {c.location_name} · Requested by {c.initiated_by_name} · {new Date(c.initiated_at).toLocaleTimeString()}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {calloutRejecting === c.id ? (
                <>
                  <input
                    value={calloutReason}
                    onChange={e => setCalloutReason(e.target.value)}
                    placeholder="Reason (required)"
                    style={{ backgroundColor: '#0f172a', border: '1px solid #ef4444', borderRadius: 6, color: '#e2e8f0', fontSize: 12, padding: '6px 10px', width: 180 }}
                  />
                  <button onClick={() => respondCallout(c.id, 'rejected', calloutReason)} disabled={!calloutReason.trim()}
                    style={{ backgroundColor: '#ef4444', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                    Confirm Reject
                  </button>
                  <button onClick={() => { setCalloutRejecting(null); setCalloutReason(''); }}
                    style={{ background: 'none', border: '1px solid #334155', borderRadius: 6, color: '#94a3b8', padding: '6px 10px', fontSize: 12, cursor: 'pointer' }}>
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button onClick={() => respondCallout(c.id, 'accepted')}
                    style={{ backgroundColor: '#16a34a', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                    Accept
                  </button>
                  <button onClick={() => setCalloutRejecting(c.id)}
                    style={{ backgroundColor: '#1e293b', color: '#ef4444', border: '1px solid #ef4444', borderRadius: 6, padding: '8px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                    Reject
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      ))}

      {/* ── Material to Receive ─────────────────────────────────── */}
      {receipts.map(s => (
        <div key={s.id} style={{ margin: '12px 20px 0', borderRadius: 10, padding: '14px 16px', backgroundColor: '#2a1e05', border: '1px solid #f59e0b' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
            <div>
              <div style={{ color: '#fbbf24', fontWeight: 800, fontSize: 13, textTransform: 'uppercase', letterSpacing: 1 }}>
                📦 Material to Receive
              </div>
              <div style={{ color: '#fde68a', fontWeight: 600, fontSize: 14, marginTop: 2 }}>
                {s.po_number ? `PO ${s.po_number}` : 'Shipment'} — {s.work_item_title}
              </div>
              <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 2 }}>
                {(s.items || []).length} item(s){s.tracking_number ? ` · ${s.tracking_number}` : ''}
                {s.delivered_at ? ` · delivered ${new Date(s.delivered_at).toLocaleDateString()}` : ''}
              </div>
            </div>
            <button onClick={() => openReceive(s)}
              style={{ backgroundColor: '#16a34a', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
              Receive &amp; Verify
            </button>
          </div>
        </div>
      ))}

      {/* Receive & Verify modal */}
      {receivingShip && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ width: '100%', maxWidth: 520, backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: 22, maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ color: '#f8fafc', fontWeight: 800, fontSize: 17, marginBottom: 4 }}>Receive &amp; Verify</div>
            <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 16 }}>
              {receivingShip.po_number ? `PO ${receivingShip.po_number}` : 'Shipment'} — {receivingShip.work_item_title}. Check what arrived against the packing list.
            </div>
            {recvItems.map((ri, idx) => {
              const orig = (receivingShip.items || []).find(x => x.id === ri.id);
              return (
                <div key={ri.id} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <span style={{ flex: 1, fontSize: 13, color: '#cbd5e1' }}>
                    {orig?.description} <span style={{ color: '#64748b' }}>(exp {orig?.expected_qty} {orig?.unit})</span>
                  </span>
                  <input type="number" value={ri.received_qty}
                    onChange={e => setRecvItems(arr => arr.map((x, i) => i === idx ? { ...x, received_qty: e.target.value } : x))}
                    style={{ width: 80, padding: '7px 9px', backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 6, color: '#e2e8f0', fontSize: 13 }} />
                  <label style={{ fontSize: 13, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <input type="checkbox" checked={ri.verified}
                      onChange={e => setRecvItems(arr => arr.map((x, i) => i === idx ? { ...x, verified: e.target.checked } : x))} /> ok
                  </label>
                </div>
              );
            })}
            <div style={{ marginTop: 14 }}>
              <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 5 }}>Signature (your name)</div>
              <input value={recvSig} onChange={e => setRecvSig(e.target.value)} placeholder="Type your name to confirm"
                style={{ width: '100%', padding: '8px 10px', backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 6, color: '#e2e8f0', fontSize: 13, boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
              <button onClick={() => setReceivingShip(null)} style={{ padding: '9px 14px', backgroundColor: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 6, cursor: 'pointer' }}>Cancel</button>
              <button onClick={submitReceive} disabled={recvBusy || !recvSig.trim()}
                style={{ padding: '9px 18px', backgroundColor: '#166534', color: '#dcfce7', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 800, opacity: !recvSig.trim() ? 0.5 : 1 }}>
                {recvBusy ? 'Saving…' : 'Confirm Received'}
              </button>
            </div>
          </div>
        </div>
      )}

      {returnTasks.length > 0 && (
        <div style={{ padding: '12px 20px 0' }}>
          {returnTasks.map(t => (
            <div key={t.id} style={{ backgroundColor: '#3a2e0a', border: '1px solid #854d0e', borderRadius: 10, padding: 14, marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <div style={{ color: '#fbbf24', fontWeight: 800, fontSize: 14 }}>📦 Return Material to Stock</div>
                <span style={{ fontSize: 11, color: t.is_capital ? '#86efac' : '#94a3b8', fontWeight: 700 }}>{t.is_capital ? 'CAPITAL WO' : 'O&M'}</span>
              </div>
              <div style={{ color: '#e2e8f0', fontSize: 13, marginBottom: 4 }}>{t.work_item_title}{t.wo_number ? ` · WO ${t.wo_number}` : ''}</div>
              <div style={{ color: '#cbd5e1', fontSize: 12, marginBottom: 8 }}>
                {(t.items || []).map(i => `${i.qty_to_return} ${i.unit || ''} ${i.description}`).join(' · ')} → {t.stock_location?.name || 'stock yard'}
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                {t.status === 'pending' ? (
                  <button onClick={() => acceptReturn(t)} style={{ padding: '7px 14px', backgroundColor: '#166534', color: '#dcfce7', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>Accept</button>
                ) : (
                  <button onClick={() => driveToStock(t)} style={{ padding: '7px 14px', backgroundColor: '#0f766e', color: '#ccfbf1', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>🧭 Start Drive to Stock</button>
                )}
                <span style={{ fontSize: 11, color: '#a16207' }}>Scan the stock-yard QR at drop-off to complete.</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ padding: '12px 0 0' }}>
        <TimerWidget items={data.items} onRefresh={load} />
      </div>

      <div style={{ borderBottom: '1px solid #1e293b', display: 'flex', padding: '0 20px', marginTop: 12 }}>
        {TABS.map(({ key, label }) => (
          <button key={key} onClick={() => setTab(key)} style={{
            padding: '12px 16px',
            border: 'none',
            borderBottom: tab === key ? '2px solid #3b82f6' : '2px solid transparent',
            backgroundColor: 'transparent',
            color: tab === key ? '#93c5fd' : '#64748b',
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: tab === key ? 800 : 600,
          }}>
            {label} ({data[key]?.length || 0})
          </button>
        ))}
      </div>

      {(error || status) && (
        <div style={{ margin: '12px 20px 0', color: error ? '#f87171' : '#22c55e', fontSize: 13, fontWeight: 700 }}>
          {error || status}
        </div>
      )}

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 20 }}>
        {tab === 'performance' ? (
          <MyPerformance />
        ) : visible.length === 0 ? (
          <div style={{ border: '1px dashed #334155', borderRadius: 8, padding: 32, textAlign: 'center', color: '#64748b' }}>
            No {TABS.find(t => t.key === tab)?.label.toLowerCase()} assigned.
          </div>
        ) : visible.map(item => (
          <WorkCard
            key={item.id}
            item={item}
            onAccept={accept}
            onReject={(next) => { setRejecting(next); setRejectReason(''); setError(''); }}
            onOpen={id => navigate(`/work/${id}`)}
          />
        ))}
      </div>

      {rejecting && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.72)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ width: '100%', maxWidth: 420, backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: 20 }}>
            <div style={{ color: '#f8fafc', fontSize: 17, fontWeight: 800, marginBottom: 6 }}>Reject Assignment</div>
            <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 14 }}>{rejecting.title}</div>
            <textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              rows={4}
              placeholder="Reason for rejection"
              style={{ width: '100%', boxSizing: 'border-box', padding: '9px 10px', backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 7, color: '#e2e8f0', fontSize: 13, resize: 'vertical' }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button onClick={() => setRejecting(null)} style={{ flex: 1, padding: 10, backgroundColor: 'transparent', color: '#94a3b8', border: '1px solid #334155', borderRadius: 6, cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={submitReject} style={{ flex: 2, padding: 10, backgroundColor: '#7f1d1d', color: '#fecaca', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 800 }}>
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
